import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/utils/logger';
import { errorHandler } from '@/utils/errorHandling';
import { behtariToast } from '@/components/ui/toast-system';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import posthog from 'posthog-js';
import { toError } from '@/utils/captureError';

/**
 * Comprehensive Error Boundary System for Behtari Platform
 * Phase 3: Code Quality - Advanced error handling with recovery options
 */

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'page' | 'section' | 'component';
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showToast?: boolean;
}

export class ComprehensiveErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryCount = 0;
  private maxRetries = 3;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { level = 'component', onError, showToast = true } = this.props;

    // Chunk-load failures are recoverable (handled by retryLazy + main.tsx
    // hard-reset path). Log them at warn level so they don't pollute
    // critical-error dashboards.
    const isChunkLoadError = /chunk load failed|loading chunk|dynamically imported module|importing a module script failed|^load failed$/i.test(
      error?.message || ''
    );
    const logFn = isChunkLoadError ? logger.warn : logger.error;

    // Wrap every side-effect below in try/catch so that a downstream failure
    // (toast provider unmounted mid-error, posthog not loaded, logger crash)
    // can never prevent the fallback UI from rendering. A boundary that
    // silently rethrows leaves the user on a blank page.
    try {
      logFn('Error Boundary caught error', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        level,
        errorId: this.state.errorId,
        retryCount: this.retryCount,
        recoverable: isChunkLoadError
      });
    } catch { /* noop */ }

    try {
      errorHandler.trackError(error, {
        boundary: level,
        componentStack: errorInfo.componentStack,
        retryCount: this.retryCount
      });
    } catch { /* noop */ }

    // Forward to PostHog error tracking in production.
    // `error` is typed as Error but React passes whatever was thrown at runtime
    // (a plain object thrown deep in the subtree — e.g. a Supabase PostgrestError
    // — arrives here as-is). Passing a non-Error to captureException stringifies
    // it to "[object Object]" in $exception_values, destroying the real content;
    // coerce it the same way captureError does.
    try {
      if (import.meta.env.PROD && posthog.__loaded) {
        posthog.captureException(toError(error), {
          boundary: level,
          errorId: this.state.errorId,
          componentStack: errorInfo.componentStack,
          retryCount: this.retryCount,
        });
      }
    } catch { /* noop */ }

    // Don't fire a toast on chunk-load errors — the full recovery UI already
    // conveys the state clearly, and a duplicate toast on top of a page-level
    // reload prompt is noise.
    if (showToast && !isChunkLoadError) {
      try {
        behtariToast.error({
          title: 'Something went wrong',
          description: level === 'page'
            ? 'The page encountered an error. Please try refreshing.'
            : 'A component failed to load. Please try again.',
          action: {
            label: 'Retry',
            onClick: () => this.handleRetry()
          }
        });
      } catch { /* noop */ }
    }

    // Call custom error handler
    if (onError) {
      try { onError(error, errorInfo); } catch { /* noop */ }
    }

    this.setState({
      errorInfo,
      error
    });
  }

  handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: null
      });
    } else {
      behtariToast.warning({
        title: 'Maximum retries reached',
        description: 'Please refresh the page or contact support if the problem persists.'
      });
    }
  };

  handleGoHome = () => {
    // Set a one-shot flag so Index.tsx skips its "authenticated → /dashboard"
    // auto-redirect for this navigation only. Without this the user bounces
    // straight back to the broken dashboard they were trying to escape.
    try {
      sessionStorage.setItem('behtari_bypass_dashboard_redirect', '1');
    } catch { /* storage blocked — landing may bounce, but reload is still an escape */ }
    try {
      window.location.assign('/');
    } catch {
      window.location.href = '/';
    }
  };

  private clearRuntimeCache = async () => {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }
  };

  handleRefresh = async () => {
    try {
      await this.clearRuntimeCache();
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('__hard_refresh', Date.now().toString());
      window.location.replace(nextUrl.toString());
    } catch {
      window.location.reload();
    }
  };

  private isStandalone(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    );
  }

  render() {
    const { hasError, error } = this.state;
    const { children, fallback, level = 'component' } = this.props;
    const standalone = this.isStandalone();

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      const isChunkLoadError = /chunk load failed|loading chunk|dynamically imported module|importing a module script failed|^load failed$/i.test(
        error?.message || ''
      );

      // Dedicated recovery UI for chunk-load failures (typical cause: a fresh
      // deploy invalidated the cached asset manifest). The generic "component
      // error" copy was misleading here and the auto-refresh handler already
      // does the right thing — clear caches + reload.
      if (isChunkLoadError) {
        return (
          <div className="flex flex-col items-center justify-center p-8 min-h-[60vh] bg-background">
            <div className="flex items-center gap-3 mb-4">
              <RefreshCw className="h-8 w-8 text-primary" />
              <h2 className="text-xl font-semibold text-foreground">
                A new version is available
              </h2>
            </div>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              We&rsquo;ve just published an update. Refresh the page to load the latest version.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button
                size="lg"
                onClick={this.handleRefresh}
                className="min-h-[48px] text-base"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Refresh
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={this.handleGoHome}
                className="min-h-[48px] text-base"
              >
                <Home className="h-5 w-5 mr-2" />
                Go Home
              </Button>
            </div>
            {import.meta.env.DEV && error && (
              <details className="mt-6 p-4 bg-muted rounded-lg max-w-2xl w-full" open>
                <summary className="cursor-pointer text-sm font-medium mb-2">
                  Error Details (Development Only)
                </summary>
                <pre className="text-xs overflow-auto whitespace-pre-wrap text-muted-foreground">
                  {error.message}
                  {error.stack ? `\n\n${error.stack}` : ''}
                </pre>
              </details>
            )}
          </div>
        );
      }

      return (
        <div className="flex flex-col items-center justify-center p-8 min-h-[50vh] bg-background border border-border rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <h2 className="text-xl font-semibold text-foreground">
              {level === 'page' ? 'Page Error' : 'Component Error'}
            </h2>
          </div>
          
          <p className="text-muted-foreground text-center mb-6 max-w-md">
            {level === 'page' 
              ? 'This page encountered an unexpected error. Please try refreshing or go back to the homepage.'
              : 'This section failed to load properly. You can try again or continue using other parts of the application.'
            }
          </p>

          {/* Always show a prominent reload button in standalone PWA mode */}
          {standalone && (
            <Button
              size="lg"
              onClick={this.handleRefresh}
              className="mb-4 min-h-[48px] text-base"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              Reload App
            </Button>
          )}

          {import.meta.env.DEV && error && (
            <details className="mb-6 p-4 bg-muted rounded-lg max-w-2xl w-full" open>
              <summary className="cursor-pointer text-sm font-medium mb-2">
                Error Details (Development Only)
              </summary>
              <pre className="text-xs overflow-auto whitespace-pre-wrap text-muted-foreground">
                {error.message}
                {error.stack ? `\n\n${error.stack}` : ''}
              </pre>
            </details>
          )}

          <div className="flex flex-wrap gap-3 justify-center">
            <Button 
              onClick={this.handleRetry}
              disabled={this.retryCount >= this.maxRetries}
              className="min-h-[44px]"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry ({this.maxRetries - this.retryCount} left)
            </Button>
            
            {!standalone && (
              <Button 
                variant="outline" 
                onClick={this.handleRefresh}
                className="min-h-[44px]"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Page
              </Button>
            )}
            
            <Button 
              variant="secondary" 
              onClick={this.handleGoHome}
              className="min-h-[44px]"
            >
              <Home className="h-4 w-4 mr-2" />
              Go Home
            </Button>
          </div>
        </div>
      );
    }

    return children;
  }
}

// Higher-order component for wrapping components with error boundaries
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ComprehensiveErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ComprehensiveErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Specialized error boundaries for different contexts
export const PageErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ComprehensiveErrorBoundary level="page" showToast={true}>
    {children}
  </ComprehensiveErrorBoundary>
);

export const SectionErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ComprehensiveErrorBoundary level="section" showToast={false}>
    {children}
  </ComprehensiveErrorBoundary>
);

export const ComponentErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ComprehensiveErrorBoundary level="component" showToast={false}>
    {children}
  </ComprehensiveErrorBoundary>
);